import React from "react";
import { Bar, Line } from "react-chartjs-2";
import Plot from "react-plotly.js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { DIVERGING, categoricalColor, divergingColor } from "./palette";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Title);

const baseOptions = (title, horizontal) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: horizontal ? "y" : "x",
  plugins: {
    legend: { display: false },
    title: { display: !!title, text: title },
  },
});

/** Renders the `chart`/`residualChart`/`rocChart` envelope fields returned by
 * analysis_engine (bar/line/scatter/roc). Color follows the JOB: diverging
 * (signed magnitude) for regression betas & odds ratios, fixed categorical
 * order for cluster identity, single sequential hue otherwise. */
function ChartRenderer({ chart }) {
  if (!chart) return null;

  if (chart.type === "bar") {
    const colors = chart.diverging
      ? chart.y.map((v) => divergingColor(chart.referenceLine ? v - chart.referenceLine : v))
      : DIVERGING.positive;
    return (
      <div className="analysis-chart">
        <Bar
          data={{ labels: chart.x, datasets: [{ data: chart.y, backgroundColor: colors }] }}
          options={baseOptions(chart.title, true)}
        />
      </div>
    );
  }

  if (chart.type === "line") {
    return (
      <div className="analysis-chart">
        <Line
          data={{
            labels: chart.x,
            datasets: [{ data: chart.y, borderColor: DIVERGING.positive, backgroundColor: "rgba(41,128,185,0.15)", fill: true, tension: 0.25 }],
          }}
          options={baseOptions(chart.title, false)}
        />
      </div>
    );
  }

  if (chart.type === "scatter") {
    const hasColor = Array.isArray(chart.color);
    const shapes = chart.zeroLine
      ? [{ type: "line", x0: Math.min(...chart.x), x1: Math.max(...chart.x), y0: 0, y1: 0, line: { color: "#9ca3af", dash: "dash", width: 1 } }]
      : [];
    return (
      <div className="analysis-chart">
        <Plot
          data={[{
            x: chart.x,
            y: chart.y,
            mode: "markers",
            type: "scattergl",
            marker: hasColor
              ? { color: chart.color.map((c) => categoricalColor(c)), size: 6 }
              : { size: 6, color: DIVERGING.positive },
          }]}
          layout={{ title: chart.title, autosize: true, margin: { t: 40 }, shapes }}
          style={{ width: "100%", height: "360px" }}
          useResizeHandler
        />
      </div>
    );
  }

  if (chart.type === "roc") {
    return (
      <div className="analysis-chart">
        <Plot
          data={[
            { x: chart.x, y: chart.y, mode: "lines", type: "scatter", line: { color: DIVERGING.positive, width: 2 }, name: "ROC" },
            { x: [0, 1], y: [0, 1], mode: "lines", type: "scatter", line: { color: "#9ca3af", dash: "dash", width: 1 }, name: "Chance", hoverinfo: "skip" },
          ]}
          layout={{
            title: chart.title, autosize: true, margin: { t: 40 }, showlegend: false,
            xaxis: { title: "False Positive Rate", range: [0, 1] },
            yaxis: { title: "True Positive Rate", range: [0, 1] },
          }}
          style={{ width: "100%", height: "360px" }}
          useResizeHandler
        />
      </div>
    );
  }

  return null;
}

export default ChartRenderer;
